import { QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, theme } from 'antd';
import { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import AdminRoute from './auth/admin-route.tsx';
import RetailerRoute from './auth/retailer-route.tsx';
import queryClient from './config/query';
import { AuthProvider } from './context/auth.context.tsx';
import { SidebarProvider } from './context/sidebar.context.tsx';
import { useThemeStore } from './store/theme';
import { colors } from './theme/colors';
import AdminDashboard from './views/admin/AdminDashboard.tsx';
import Devices from './views/admin/Devices.tsx';
import Login from './views/admin/Login.tsx';
import Retailers from './views/admin/Retailers.tsx';
import SystemConfig from './views/admin/SystemConfig.tsx';
import Transactions from './views/admin/Transactions.tsx';
import Users from './views/admin/Users.tsx';
import DashboardRedirect from './views/DashboardRedirect.tsx';
import VendorLogin from './views/retailer/RetailerLogin.tsx';
import RetailerDevices from './views/retailer/RetailerDevices.tsx';
import ForgotPassword from './views/ForgotPassword.tsx';
import RetailerDashboard from './views/retailer/RetailerDashboard.tsx';
import PaymentAccounts from './views/retailer/PaymentAccounts.tsx';
import Withdrawals from './views/retailer/Withdrawals.tsx';
import CreateAdmin from './views/admin/CreateAdmin.tsx';
import Calculator from './views/public/Calculator.tsx';
import QRCode from './views/public/QRCode.tsx';
import LockScreenPayment from './views/public/LockScreenPayment.tsx';
import Installments from './views/admin/Installments.tsx';
import Reports from './views/admin/Reports.tsx';
import DeviceReportV1 from './views/admin/DeviceReportV1.tsx';
import AgentOverdueTracker from './views/admin/AgentOverdueTracker.tsx';
import VerificationTracker from './views/admin/VerificationTracker.tsx';
import FinancialReport from './views/admin/FinancialReport.tsx';
import ManageAdmins from './views/admin/ManageAdmins.tsx';
import FieldRetailerApplications from './views/admin/FieldRetailerApplications.tsx';
import FieldClaims from './views/admin/FieldClaims.tsx';
import FieldEarningRates from './views/admin/FieldEarningRates.tsx';
import FieldAgents from './views/admin/FieldAgents.tsx';
import FieldActivityReport from './views/admin/FieldActivityReport';
import FieldCommissions from './views/admin/FieldCommissions';
import WrittenOffReport from './views/admin/WrittenOffReport';
import WidgetTokens from './views/admin/WidgetTokens.tsx';
import ManageDevices from './views/admin/ManageDevices.tsx';
import ForRelease from './views/admin/ForRelease.tsx';
import Reconciliation from './views/admin/Reconciliation.tsx';
import RevenueReport from './views/admin/RevenueReport.tsx';
import SalesReport from './views/admin/SalesReport.tsx';
import SalesReportDevices from './views/admin/SalesReportDevices.tsx';
import OverdueReport from './views/admin/OverdueReport.tsx';
import PayoutsReport from './views/admin/PayoutsReport.tsx';
import Payouts from './views/admin/Payouts.tsx';
import InsuranceReport from './views/admin/InsuranceReport.tsx';
import InsuranceReports from './views/admin/InsuranceReports.tsx';
import AdminLogs from './views/admin/AdminLogs.tsx';
import RecoveredDevices from './views/admin/RecoveredDevices.tsx';
import CommissionReport from './views/admin/CommissionReport.tsx';

const router = createBrowserRouter([
	{
		path: '/',
		element: <DashboardRedirect />,
	},
	{
		path: '/login',
		element: <Login />,
	},

	{
		path: '/retailer/login',
		element: <VendorLogin />,
	},
	{
		path: '/forgot-password',
		element: <ForgotPassword />,
	},
	{
		path: '/calculator',
		element: <Calculator />,
	},
	{
		path: '/qr-code',
		element: <QRCode />,
	},
	{
		path: '/lock-screen-payment',
		element: <LockScreenPayment />,
	},
	{
		element: <AdminRoute />,
		children: [
			{
				path: '/admin/dashboard',
				element: <AdminDashboard />,
			},
			{
				path: '/admin/users',
				element: <Users />,
			},
			{
				path: '/admin/devices',
				element: <Devices />,
			},
			{
				path: '/admin/installments',
				element: <Installments />,
			},
			{
				path: '/admin/transactions',
				element: <Transactions />,
			},
			{
				path: '/admin/retailers',
				element: <Retailers />,
			},
			{
				path: '/admin/config',
				element: <SystemConfig />,
			},
			{
				path: '/admin/create-admin',
				element: <CreateAdmin />,
			},
			{
				path: '/admin/payouts',
				element: <Payouts />,
			},
			{
				path: '/admin/reports',
				element: <Reports />,
			},
			{
				path: '/admin/reports/device-report-v1',
				element: <DeviceReportV1 />,
			},
			{
				path: '/admin/reports/revenue-report',
				element: <RevenueReport />,
			},
		{
				path: '/admin/reports/financial',
				element: <FinancialReport />,
			},
		{
				path: '/admin/agent-overdue-tracker',
				element: <AgentOverdueTracker />,
			},
		{
				path: '/admin/verification-tracker',
				element: <VerificationTracker />,
			},
			{
				path: '/admin/manage-admins',
				element: <ManageAdmins />,
			},
			{
				path: '/admin/field/retailer-applications',
				element: <FieldRetailerApplications />,
			},
			{
				path: '/admin/field/claims',
				element: <FieldClaims />,
			},
			{
				path: '/admin/field/earning-rates',
				element: <FieldEarningRates />,
			},
			{
				path: '/admin/field/team',
				element: <FieldAgents />,
			},
			{
				path: '/admin/field/activity',
				element: <FieldActivityReport />,
			},
			{
				path: '/admin/field/commissions',
				element: <FieldCommissions />,
			},
			{
				path: '/admin/reports/written-off',
				element: <WrittenOffReport />,
			},
			{
				path: '/admin/widget-tokens',
				element: <WidgetTokens />,
			},
			{
				path: '/admin/manage-devices',
				element: <ManageDevices />,
			},
			{
				path: '/admin/for-release',
				element: <ForRelease />,
			},
			{
				path: '/admin/reconciliation',
				element: <Reconciliation />,
			},
			{
				path: '/admin/reports/sales',
				element: <SalesReport />,
			},
			{
				path: '/admin/reports/sales/devices',
				element: <SalesReportDevices />,
			},
			{
				path: '/admin/reports/overdue',
				element: <OverdueReport />,
			},
			{
				path: '/admin/reports/payouts',
				element: <PayoutsReport />,
			},
			{
				path: '/admin/reports/insurance',
				element: <InsuranceReport />,
			},
			{
				path: '/admin/reports/commission',
				element: <CommissionReport />,
			},
			{
				path: '/admin/admin-logs',
				element: <AdminLogs />,
			},
			{
				path: '/insurance/reports',
				element: <InsuranceReports />,
			},
			{
				path: '/admin/recovered-devices',
				element: <RecoveredDevices />,
			},
		],
	},
	{
		element: <RetailerRoute />,
		children: [
			{
				path: '/retailer/dashboard',
				element: <RetailerDashboard />,
			},
			{
				path: '/retailer/devices',
				element: <RetailerDevices />,
			},
			{
				path: '/retailer/withdrawals',
				element: <Withdrawals />,
			},
			{
				path: '/retailer/payment-accounts',
				element: <PaymentAccounts />,
			},
		],
	},
	{
		path: '*',
		element: <div>Not Found</div>,
	},
]);

export default function App() {
	// controlling the dark mode
	const { is_dark_mode } = useThemeStore();

	useEffect(() => {
		if (is_dark_mode) {
			document.documentElement.classList.add('dark');
		} else {
			document.documentElement.classList.remove('dark');
		}
	}, [is_dark_mode]);

	return (
		<ConfigProvider
			theme={{
				token: {
					colorPrimary: colors.primary,
					fontFamily: 'Satoshi, sans-serif',
					colorBgContainer: is_dark_mode ? '#161422' : '#ffffff',
					colorBgElevated: is_dark_mode ? '#161422' : '#ffffff',
				},
				components: {
					Button: {
						colorPrimary: colors.primary,
						defaultBg: is_dark_mode ? '#1b1827' : '#ffffff',
						colorBgContainerDisabled: is_dark_mode ? '#161422' : '#f5f5f5',
					},
					Badge: {
						colorPrimary: colors.primary,
						colorBgBase: colors.primary,
						colorPrimaryActive: colors.primary,
						dotSize: 12,
					},
					Carousel: {
						dotHeight: 12,
						dotActiveWidth: 12,
						dotWidth: 12,
						colorBgContainer: colors.primary,
					},
					Card: {
						colorBgContainer: is_dark_mode ? 'transparent' : '#ffffff',
					},
				},
				algorithm: is_dark_mode ? theme.darkAlgorithm : theme.defaultAlgorithm,
			}}
			wave={{
				disabled: true,
			}}
		>
			<QueryClientProvider client={queryClient}>
				<AuthProvider>
					<SidebarProvider>
						<RouterProvider router={router} />
						<Toaster
							toastOptions={{
								className: 'font-medium bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
							}}
						/>
					</SidebarProvider>
				</AuthProvider>
			</QueryClientProvider>
		</ConfigProvider>
	);
}
